import { NgModule, ErrorHandler, provideZoneChangeDetection } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HttpClientModule, HttpClientXsrfModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { RouterModule, PreloadAllModules } from '@angular/router';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

/*
 * Platform and Environment providers/directives/pipes
 */
import { ROUTES } from './app.routes';
/*
 * Modules
 */
import { FocModule } from './../foc/foc.module';
/*
 * Components
 */
// App is our top level component
import { App } from './components/main/app.component';
import { AppCloseModal } from './components/app-close-modal/app-close-modal.component';
import { AssetErrorModal } from './components/asset-error-modal/asset-error-modal.component'
import { AutoupdateErrorModal } from './components/autoupdate-error-modal/autoupdate-error-modal.component';
import { DownloadingErrorModal } from './components/downloading-error-modal/downloading-error-modal.component';
/*
 * Services
 */
import { CustomErrorHandler } from './services/customErrorHandler.service';
import { CustomHttpInterceptor } from './services/customHttpInterceptor.service';
import { ApiGatewayHelperService } from './services/apigateway-helper.service';
import { AuthService } from './services/auth.service';
import { GigyaSessionService } from './services/gigya-session.service';
import { AuthenticatedRoutesGuard } from './services/authenticated-routes-guard.service';
import { CustomTranslationLoader } from './services/custom-translation-loader';
import { LoaderService } from './services/loader.service';
import { CacheService } from './services/cache.service';
import { CustomBackendLoggerService } from './services/custom-backend-logger.service';
import { CustomArrayOpsService } from './services/custom-array-ops.service';
import { AppHelperService } from './services/app-helper.service';
import { EventService } from './services/event.service';
import { DesktopAppEventsService } from './services/desktop-app-events-service';
import { EreaderEventService } from './services/eReader-event.service';
import { NetwordDetectionService } from './services/network-detection.service';
import { MessageService } from './services/message.service';
import { HtmlMetaManagerService } from './services/html-meta-manager.service';
import { CustomPubSub } from './services/custom-pubsub.service';
import { CreateUserAnalyticsService } from './services/create-user-analytics.service';


// service used for desktop app state tracking
import { AppStateHandlerService } from './services/app-state-handler.service';
import { DownloadHelperService } from 'modules/desktop/services/download-helper.service';
import { HelperService } from 'modules/desktop/services/helper.service';
import { DesktopEBookVMService } from 'modules/desktop/services/desktop-ebook-vm.service';
import { DesktopGlobalStateService } from 'modules/desktop/services/desktop-global-state.service';
import { ActivateComponentService } from 'modules/desktop/services/activate-component.service';
import { DesktopUserPreferencesService } from 'modules/desktop/services/desktop-user-preferences-data.service';
import { DesktopDrawingsDataService } from 'modules/desktop/services/desktop-drawings-data.service';
import { DesktopNotesDataService } from 'modules/desktop/services/desktop-notes-data.service';
import { DesktopUserDataService } from 'modules/desktop/services/desktop-user-data.service';
import { UserdataService } from 'modules/desktop/services/userdata.service';
import { DesktopAuthService } from 'modules/desktop/services/desktop-auth.service';
import { EbookHelperService } from 'modules/desktop/services/ebook-helper.service';
import { DesktopDatabaseHelperService } from 'modules/desktop/services/desktop-database-helper.service';
import { LearnerDashboardService } from 'modules/desktop/services/learner-dashboard.service';
import { ClassEnrollmentService } from 'modules/desktop/services/class-enrollments.service';
import { LearnerHelperService } from 'modules/desktop/services/learner-helper.service';
import { ProductEntitlementsService } from 'modules/desktop/services/product-entitlements.service';
import { StudentBookService } from 'modules/desktop/services/student-book.service';
import { TeacherHelperService } from 'modules/desktop/services/teacher-helper.service';
import { UmbrellaService } from 'modules/desktop/services/umbrella.service';
import { ServerSyncService } from 'modules/desktop/services/server-sync.service';
import { PushXService } from 'modules/shared-postlogin/services/pushx.service';
import { ResourceBankHelperService } from './services/resource-bank-helper.service';

import cavClient from 'libs-cavalier-integration/frontend';

// initialize cavalier client
cavClient.init('foc')
cavClient.capturePageViewEvents();

/**
 * `AppModule` is the main entry point into Angular2's bootstraping process
 */
@NgModule({
  bootstrap: [ App ],
  declarations: [
    App,
    AppCloseModal,
    AssetErrorModal,
    AutoupdateErrorModal,
    DownloadingErrorModal,
  ],
  /**
   * Import Angular's modules.
   */
  imports: [
    BrowserModule,
    CommonModule,
    BrowserAnimationsModule,
    FormsModule,
    HttpClientModule,
    HttpClientXsrfModule.withOptions({
      cookieName: 'csrf-token',
      headerName: 'csrf-token'
    }),
    RouterModule.forRoot(ROUTES, {
    preloadingStrategy: PreloadAllModules
}),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader, 
        useClass: CustomTranslationLoader
      }
    }),
    FocModule
  ],
  /**
   * Expose our Services and Providers into Angular's dependency injection.
   */
  providers: [
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: CustomHttpInterceptor, multi: true } ,
    AuthService,
    ApiGatewayHelperService,
    GigyaSessionService,
    CustomTranslationLoader,
    CustomPubSub,
    AuthenticatedRoutesGuard,
    LoaderService,
    CacheService,
    CustomBackendLoggerService,
    AppHelperService,
    EventService,
    DesktopAppEventsService,
    EreaderEventService,
    MessageService,
    NetwordDetectionService,
    AppStateHandlerService,
    HtmlMetaManagerService,
    CustomArrayOpsService,
    DownloadHelperService,
    HelperService,
    DesktopEBookVMService,
    DesktopGlobalStateService,
    ActivateComponentService,
    DesktopUserPreferencesService,
    DesktopDrawingsDataService,
    DesktopNotesDataService,
    DesktopUserDataService,
    UserdataService,
    DesktopAuthService,
    EbookHelperService,
    DesktopDatabaseHelperService,
    LearnerDashboardService,
    ClassEnrollmentService,
    LearnerHelperService,
    ProductEntitlementsService,
    StudentBookService,
    TeacherHelperService,
    UmbrellaService,
    ServerSyncService,
    CreateUserAnalyticsService,
    PushXService,
    ResourceBankHelperService,
    provideZoneChangeDetection({ eventCoalescing: true })
  ]
})
export class AppModule {}
